import { ReactNode } from "react";
import { Box, Paper, Table, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Tabla del Design Kit (§07): contenedor blanco con borde y radio 9, cabecera
 * azul clara en Poppins mayúsculas, filas separadas por hairline y hover suave.
 *
 * Uso:
 *   <TablaKit columnas={["Título", "Estado"]} minWidth={720}>
 *     {filas.map((f) => (
 *       <FilaKit key={f.id} onClick={...}>
 *         <CeldaPrincipal>{f.titulo}</CeldaPrincipal>
 *         <CeldaKit>…</CeldaKit>
 *       </FilaKit>
 *     ))}
 *   </TablaKit>
 */
export interface Columna {
  label: string;
  align?: "left" | "right";
  /** Si se define (y la tabla recibe `onSort`), la cabecera es ordenable por esta clave. */
  sortKey?: string;
  /** Ancho fijo/sugerido de la columna (px o CSS), para columnas de contenido corto. */
  width?: number | string;
  /**
   * Columna de control (una casilla de selección), sin texto: reduce el aire
   * lateral. Va aquí y no solo en la celda para que la cabecera no siga
   * reservando el ancho de una columna de texto.
   */
  compacta?: boolean;
}

/** Estado de orden que recibe la tabla; compatible con `Orden` de utils/orden. */
export interface OrdenTabla {
  key: string;
  dir: "asc" | "desc";
}

export function TablaKit({
  columnas,
  children,
  minWidth = 760,
  orden,
  onSort,
  anchosFijos = false,
  etiqueta = "Tabla",
}: {
  columnas: (string | Columna)[];
  children: ReactNode;
  minWidth?: number;
  orden?: OrdenTabla | null;
  onSort?: (key: string) => void;
  /** Nombre de la región desplazable, para quien navega con lector de pantalla. */
  etiqueta?: string;
  /**
   * Reparte el ancho por lo declarado en `width` y no por el contenido
   * (`table-layout: fixed`). Sin esto un título largo se come el espacio de las
   * demás columnas y los porcentajes quedan en sugerencia.
   */
  anchosFijos?: boolean;
}) {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      // Desplazable con teclado: en pantallas angostas la tabla es más ancha que
      // su caja y las últimas columnas —donde están las acciones— solo se
      // alcanzan desplazándola. `overflow: hidden` (por el radio) las dejaba
      // fuera de alcance; recortar solo en vertical mantiene las esquinas.
      tabIndex={0}
      role="region"
      aria-label={etiqueta}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "9px",
        overflowX: "auto",
        overflowY: "hidden",
      }}
    >
      <Table sx={{ minWidth, tableLayout: anchosFijos ? "fixed" : undefined }}>
        <TableHead>
          <TableRow sx={{ bgcolor: tokens.tabla.cabeceraBg }}>
            {columnas.map((c) => {
              const col: Columna = typeof c === "string" ? { label: c } : c;
              const ordenable = !!(col.sortKey && onSort);
              const activa = ordenable && orden?.key === col.sortKey;
              return (
                <TableCell
                  key={col.label}
                  align={col.align ?? "left"}
                  sortDirection={activa ? orden!.dir : false}
                  sx={{
                    fontFamily: (t) => t.typography.h6.fontFamily,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: ".04em",
                    textTransform: "uppercase",
                    color: tokens.tabla.cabeceraTexto,
                    border: 0,
                    padding: col.compacta ? "14px 4px 14px 12px" : "14px 22px",
                    width: col.width,
                    whiteSpace: col.width ? "nowrap" : undefined,
                  }}
                >
                  {ordenable ? (
                    <TableSortLabel
                      active={activa}
                      direction={activa ? orden!.dir : "asc"}
                      onClick={() => onSort!(col.sortKey!)}
                      sx={{
                        // Hereda el color de la cabecera (activa o no) en vez de los grises de MUI.
                        color: "inherit !important",
                        "& .MuiTableSortLabel-icon": { color: "inherit !important", opacity: activa ? 0.9 : 0.35 },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        {children}
      </Table>
    </TableContainer>
  );
}

/**
 * Fila con el hairline y el hover del kit. `onClick` añade el cursor.
 *
 * `destacada` tiñe el fondo con la marca tenue. `destacadaEnHover` reserva ese
 * tono para el hover: la fila queda neutra en reposo y solo se tiñe al pasar por
 * encima (útil cuando *todas* las filas son destacables y el tono sólido cansa).
 *
 * Si la fila es clicable, también se opera con el teclado: abrir una ficha es la
 * navegación principal de estas tablas y con el ratón como única vía queda fuera
 * de alcance para quien no lo usa.
 */
export function FilaKit({
  children,
  onClick,
  destacada,
  destacadaEnHover,
}: {
  children: ReactNode;
  /**
   * Recibe el evento para que quien navega pueda mirar los modificadores: una
   * `<tr>` no puede ir dentro de un `<a>`, así que Ctrl/⌘+clic hay que
   * reconocerlo a mano (ver `utils/enlaces.pideOtraPestana`).
   */
  onClick?: (e: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; button?: number }) => void;
  destacada?: boolean;
  destacadaEnHover?: boolean;
}) {
  return (
    <TableRow
      onClick={onClick}
      // Botón central: el otro gesto de "abrir en pestaña nueva".
      onAuxClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={{
        borderTop: `1px solid ${tokens.tabla.filaBorde}`,
        bgcolor: destacada ? tokens.marca.tenue : undefined,
        cursor: onClick ? "pointer" : undefined,
        "&:hover": { bgcolor: destacadaEnHover ? tokens.marca.tenue : tokens.tabla.filaHover },
        "&:focus-visible": {
          outline: `2px solid ${tokens.marca.texto}`,
          outlineOffset: "-2px",
          bgcolor: tokens.tabla.filaHover,
        },
      }}
    >
      {children}
    </TableRow>
  );
}

const celdaBase = { padding: "16px 22px", border: 0, fontSize: 14 } as const;

/**
 * Celda normal. `atenuada` para valores vacíos ("—"); `lineas` recorta el texto
 * a ese número de líneas y lo termina en "…" (el clamp va en un span interno,
 * porque cambiar el `display` del `<td>` rompería la tabla).
 *
 * `compacta` reduce el aire lateral para las columnas que no llevan texto sino un
 * control —la casilla de selección—: con el padding normal (22 px por lado) una
 * casilla de 18 px ocupa una columna de texto entera.
 */
export function CeldaKit({
  children,
  align,
  atenuada,
  noWrap,
  maxWidth,
  lineas,
  compacta,
}: {
  children: ReactNode;
  align?: "left" | "right";
  atenuada?: boolean;
  noWrap?: boolean;
  maxWidth?: number;
  lineas?: number;
  compacta?: boolean;
}) {
  return (
    <TableCell
      align={align ?? "left"}
      sx={{
        ...celdaBase,
        ...(compacta ? { padding: "16px 4px 16px 12px" } : {}),
        color: atenuada ? "text.disabled" : tokens.tabla.celda,
        whiteSpace: noWrap ? "nowrap" : undefined,
        maxWidth,
      }}
    >
      {lineas ? (
        <Box
          component="span"
          sx={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: lineas,
            overflow: "hidden",
            whiteSpace: "normal",
          }}
        >
          {children}
        </Box>
      ) : (
        children
      )}
    </TableCell>
  );
}

/** Primera columna: peso 600 y azul oscuro (kit §07). */
export function CeldaPrincipal({ children, maxWidth }: { children: ReactNode; maxWidth?: number }) {
  return <TableCell sx={{ ...celdaBase, fontWeight: 600, color: tokens.tabla.celdaFuerte, maxWidth }}>{children}</TableCell>;
}
